// Keep in sync with supabase/functions/_shared/labImport/overviewCurrent.ts.
// Coolify builds web/ alone, so this file must not import outside web/.

/**
 * Read-time current-row selection for Svaroversigt (XRPT05 2.4–2.6 / 3.25–3.27).
 * Import still versions by result_key (sampleId|code) and never deletes rows.
 * attachLetterOwnedMicro may put a superseded letter back into investigations
 * so the card can render; those rows must not appear as current overview rows
 * once a svar_endeligt exists for the same clinic/patient/analysis.
 */

import { isFinalStatus, isPreliminaryStatus, shouldBecomeCurrent } from './labVersioning';

export type OverviewCurrentRow = {
  id: string;
  report_id: string;
  result_key: string;
  result_status_code?: string | null;
  results_datetime?: string | null;
  mic_analysis_code?: string | null;
  analysis_code?: string | null;
};

export type OverviewReportMeta = {
  id: string;
  requester_sample_id?: string | null;
};

export function isLetterOwnedId(id: string | null | undefined): boolean {
  return typeof id === 'string' && id.startsWith('letter:');
}

export function analysisCodeFromResultKey(resultKey: string | null | undefined): string {
  if (!resultKey) return '';
  const sep = resultKey.indexOf('|');
  return (sep >= 0 ? resultKey.slice(sep + 1) : resultKey).trim().toLowerCase();
}

export function analysisCodeOf(row: Pick<OverviewCurrentRow, 'result_key' | 'mic_analysis_code' | 'analysis_code'>): string {
  const fromFields = (row.mic_analysis_code ?? row.analysis_code ?? '').trim().toLowerCase();
  return fromFields || analysisCodeFromResultKey(row.result_key);
}

export function requisitionAnalysisKey(
  requesterSampleId: string | null | undefined,
  analysisCode: string | null | undefined,
): string | null {
  const req = (requesterSampleId ?? '').trim().toLowerCase();
  const code = (analysisCode ?? '').trim().toLowerCase();
  if (!req || !code) return null;
  return `${req}|${code}`;
}

function asVersion(row: OverviewCurrentRow): { resultsDateTime: string | null; resultStatusCode: string | null } {
  return {
    resultsDateTime: row.results_datetime ?? null,
    resultStatusCode: row.result_status_code ?? null,
  };
}

export function pickCurrentByResultKey<T extends OverviewCurrentRow>(rows: readonly T[]): Map<string, T> {
  const map = new Map<string, T>();
  for (const row of rows) {
    const existing = map.get(row.result_key);
    if (!existing) {
      map.set(row.result_key, row);
      continue;
    }
    if (shouldBecomeCurrent(asVersion(row), asVersion(existing))) {
      map.set(row.result_key, row);
      continue;
    }
    if (
      !shouldBecomeCurrent(asVersion(existing), asVersion(row)) &&
      isLetterOwnedId(existing.id) &&
      !isLetterOwnedId(row.id)
    ) {
      map.set(row.result_key, row);
    }
  }
  return map;
}

function reportById(reports: readonly OverviewReportMeta[]): Map<string, OverviewReportMeta> {
  return new Map(reports.map((report) => [report.id, report]));
}

function requisitionKeyFor(
  row: Pick<OverviewCurrentRow, 'report_id' | 'result_key' | 'mic_analysis_code' | 'analysis_code'>,
  reports: Map<string, OverviewReportMeta>,
): string | null {
  return requisitionAnalysisKey(reports.get(row.report_id)?.requester_sample_id, analysisCodeOf(row));
}

/**
 * One current row per result_key. A later svar_endeligt / svar_rettet keeps
 * the provisional (and a letter-hydrated copy of it) out of the overview.
 *
 * XRPT05 2.4 Modtaget: proeve_modtaget is also dropped when a final exists
 * for the same requester sample + analysis, even if the lab assigned a new
 * LaboratoryInternalSampleIdentifier (different result_key).
 */
export function currentOverviewRows<T extends OverviewCurrentRow>(
  rows: readonly T[],
  reports: readonly OverviewReportMeta[],
): T[] {
  const picked = [...pickCurrentByResultKey(rows).values()];
  const byReport = reportById(reports);
  const finalsByRequisition = new Set<string>();
  for (const row of picked) {
    if (!isFinalStatus(row.result_status_code)) continue;
    const key = requisitionKeyFor(row, byReport);
    if (key) finalsByRequisition.add(key);
  }
  return picked.filter((row) => {
    if (row.result_status_code !== 'proeve_modtaget') return true;
    const key = requisitionKeyFor(row, byReport);
    return !(key && finalsByRequisition.has(key));
  });
}

export function findRequisitionReplacement<T extends OverviewCurrentRow>(
  item: Pick<OverviewCurrentRow, 'report_id' | 'result_key' | 'result_status_code' | 'mic_analysis_code' | 'analysis_code'>,
  rows: readonly T[],
  reports: readonly OverviewReportMeta[],
): T | null {
  if (!isPreliminaryStatus(item.result_status_code)) return null;
  const byReport = reportById(reports);
  const itemKey = requisitionKeyFor(item, byReport);
  if (!itemKey) return null;
  let winner: T | null = null;
  for (const row of pickCurrentByResultKey(rows).values()) {
    if (row.report_id === item.report_id) continue;
    if (!isFinalStatus(row.result_status_code)) continue;
    if (requisitionKeyFor(row, byReport) !== itemKey) continue;
    if (!winner || shouldBecomeCurrent(asVersion(row), asVersion(winner))) {
      winner = row;
    }
  }
  return winner;
}
